import React, { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Icon, Target, Zap, KeyRound, Handshake, MoveRight, Footprints, XCircle, Trophy } from 'lucide-react';
import { soccerBall } from '@lucide/lab';
import { useAuth } from '@/contexts/AuthContext';
import { fetchUpcomingContests } from '@/api/dfs';
import { mixpanelTrack } from '@/services/mixpanel';
import HowToPlayLeaguesCarousel from '@/components/scoring/HowToPlayLeaguesCarousel';
import { compareContestsCombinedFirst } from '@/utils/contestLobby';

// Horizontal swipe carousel with active-card emphasis.
// Active card = the card whose center is closest to the scroll container's center.
// On desktop, non-active cards fade out, become clickable to jump-to, and arrow
// buttons offer explicit prev/next nav. On mobile, opacity stays full so peeking
// cards remain readable.
const Carousel: React.FC<{ children: ReactNode[]; ariaLabel: string }> = ({ children, ariaLabel }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActiveFromScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerCenter = container.scrollLeft + container.offsetWidth / 2;
    const cards = container.querySelectorAll<HTMLElement>('[data-card]');
    let closestIndex = 0;
    let closestDistance = Infinity;
    cards.forEach((card, idx) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - containerCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });
    setActiveIndex(closestIndex);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateActiveFromScroll, { passive: true });
    updateActiveFromScroll();
    return () => container.removeEventListener('scroll', updateActiveFromScroll);
  }, [updateActiveFromScroll, children.length]);

  const scrollToIndex = (idx: number) => {
    const container = containerRef.current;
    if (!container) return;
    const card = container.querySelector(`[data-index="${idx}"]`) as HTMLElement | null;
    card?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const goPrev = () => scrollToIndex(Math.max(0, activeIndex - 1));
  const goNext = () => scrollToIndex(Math.min(children.length - 1, activeIndex + 1));
  const atStart = activeIndex === 0;
  const atEnd = activeIndex === children.length - 1;

  return (
    <div className="relative">
      {/* Desktop nav arrows */}
      <button
        onClick={goPrev}
        disabled={atStart}
        aria-label="Previous card"
        className="hidden md:flex absolute left-[calc(50%-220px)] top-[180px] -translate-x-full -translate-y-1/2 z-10 items-center justify-center w-11 h-11 rounded-full bg-primary border-2 border-accent-yellow/80 text-accent-yellow shadow-lg hover:bg-primary hover:border-accent-yellow disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={goNext}
        disabled={atEnd}
        aria-label="Next card"
        className="hidden md:flex absolute right-[calc(50%-220px)] top-[180px] translate-x-full -translate-y-1/2 z-10 items-center justify-center w-11 h-11 rounded-full bg-primary border-2 border-accent-yellow/80 text-accent-yellow shadow-lg hover:bg-primary hover:border-accent-yellow disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <ChevronRight size={22} />
      </button>

      <div
        ref={containerRef}
        role="region"
        aria-label={ariaLabel}
        className="flex gap-3 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-[calc(50%-140px)] sm:px-[calc(50%-150px)] md:px-[calc(50%-160px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children.map((child, idx) => {
          const isActive = idx === activeIndex;
          return (
            <div
              key={idx}
              data-card
              data-index={idx}
              onClick={() => !isActive && scrollToIndex(idx)}
              className={`snap-center flex-shrink-0 w-[280px] sm:w-[300px] md:w-[320px] transition-all duration-300 ${
                isActive
                  ? 'md:opacity-100 md:scale-100'
                  : 'md:opacity-30 md:scale-95 md:cursor-pointer md:hover:opacity-60'
              }`}
            >
              {child}
            </div>
          );
        })}
      </div>

      <div className="flex justify-center gap-1.5 mt-2">
        {children.map((_, idx) => (
          <button
            key={idx}
            onClick={() => scrollToIndex(idx)}
            aria-label={`Go to card ${idx + 1}`}
            className={`h-2 rounded-full transition-all duration-200 ${
              idx === activeIndex ? 'w-6 bg-accent-yellow' : 'w-2 bg-gray-600 hover:bg-gray-500'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Generic card shell — fixed height, dark bg, top border accent.
const CardShell: React.FC<{ accentColor?: string; children: ReactNode }> = ({ accentColor = '#ffc42c', children }) => (
  <div
    className="bg-surface-card rounded-xl border border-gray-700/60 h-[420px] flex flex-col p-5 relative overflow-hidden"
    style={{ borderTopWidth: '3px', borderTopColor: accentColor }}
  >
    {children}
  </div>
);

// Compact lineup row — fits 8 rows in a single card. Shows position chip + player slot.
const MiniLineupRow: React.FC<{
  position: string;
  primary: string;
  secondary?: string;
  highlight?: 'salary' | 'score' | 'empty';
}> = ({ position, primary, secondary, highlight = 'salary' }) => {
  const valueColor =
    highlight === 'score' ? 'text-accent-yellow' : highlight === 'empty' ? 'text-gray-500' : 'text-white';
  return (
    <div className="flex items-center bg-primary/60 rounded px-2 py-1 border border-accent-yellow/15">
      <span className="text-micro font-bold text-accent-yellow w-10 text-center flex-shrink-0">{position}</span>
      <span
        className={`flex-grow text-caption font-medium truncate ${
          highlight === 'empty' ? 'text-gray-500 italic' : 'text-gray-100'
        }`}
      >
        {primary}
      </span>
      {secondary && <span className={`text-caption font-bold flex-shrink-0 ml-2 ${valueColor}`}>{secondary}</span>}
    </div>
  );
};

// Demo lineup data — matches the homepage hero SVG so the story is consistent.
// Salaries sum to EXACTLY $100,000 (the stated budget — the filled card claims
// "$0 Remaining"). If you edit any salary, rebalance so the total stays 100,000
// and mirror the change in public/hero-lineup-comparison.svg (both panels).
const DEMO_LINEUP = [
  { position: 'FW', name: 'E. Haaland', salary: '$13,500', score: '22.0' },
  { position: 'FW', name: 'B. Saka', salary: '$14,800', score: '24.0' },
  { position: 'MF', name: 'Bruno Fernandes', salary: '$16,800', score: '28.5' },
  { position: 'MF', name: 'Rodri', salary: '$12,900', score: '14.0' },
  { position: 'DF', name: 'H. Maguire', salary: '$8,900', score: '8.5' },
  { position: 'DF', name: 'V. van Dijk', salary: '$9,800', score: '11.5' },
  { position: 'FLEX', name: 'M. Salah', salary: '$12,000', score: '18.5' },
  { position: 'FLEX', name: 'P. Foden', salary: '$11,300', score: '16.0' },
];

// === HOW TO PLAY CARDS ===

const EmptyLineupCard: React.FC = () => (
  <CardShell>
    <div className="text-center mb-3">
      <p className="text-badge font-bold uppercase tracking-widest text-white mb-0.5">Your Budget</p>
      <p className="text-3xl font-heading font-bold text-accent-yellow leading-tight">$100,000</p>
    </div>
    <div className="space-y-1 flex-grow">
      {DEMO_LINEUP.map((p, i) => (
        <MiniLineupRow key={i} position={p.position} primary="Empty" secondary="$0" highlight="empty" />
      ))}
    </div>
    <p className="text-caption font-bold text-white text-center mt-3 leading-snug">
      <span className="text-accent-yellow">$100,000 budget</span> to build out your lineup. Each player has a salary. Each lineup must have 2 FW, 2 MF, 2 DF, and 2 FLEX.
    </p>
  </CardShell>
);

const FilledLineupCard: React.FC = () => (
  <CardShell accentColor="#e24b4a">
    <div className="text-center mb-3">
      <p className="text-badge font-bold uppercase tracking-widest text-white mb-0.5">Build Your Lineup</p>
      <p className="text-2xl font-heading font-bold text-accent-yellow leading-tight">$0 Remaining</p>
    </div>
    <div className="space-y-1 flex-grow">
      {DEMO_LINEUP.map((p, i) => (
        <MiniLineupRow key={i} position={p.position} primary={p.name} secondary={p.salary} />
      ))}
    </div>
    <div className="mt-3 py-1.5 rounded bg-accent-yellow text-primary text-center text-caption font-heading font-bold uppercase tracking-wider">
      Submit Lineup
    </div>
  </CardShell>
);

const ScoredLineupCard: React.FC = () => (
  <CardShell accentColor="#78b4e6">
    <div className="text-center mb-3">
      <p className="text-badge font-bold uppercase tracking-widest text-white mb-0.5 flex items-center justify-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
        </span>
        Scores update in real-time
      </p>
      <p className="text-2xl font-heading font-bold text-accent-yellow leading-tight">143.0 PTS</p>
    </div>
    <div className="space-y-1 flex-grow">
      {DEMO_LINEUP.map((p, i) => (
        <MiniLineupRow key={i} position={p.position} primary={p.name} secondary={p.score} highlight="score" />
      ))}
    </div>
  </CardShell>
);

// Leaderboard data for the demo — user wins, 5 others below (same league-member
// cast as the Leagues carousel, owner 2026-07-22; capped at 6 rows)
const DEMO_LEADERBOARD = [
  { rank: 1, user: 'You', score: '143.0', highlight: true },
  { rank: 2, user: 'Diamond Dogs', score: '138.5', highlight: false },
  { rank: 3, user: 'Tony Perkis', score: '132.0', highlight: false },
  { rank: 4, user: 'Three Comma Club', score: '128.5', highlight: false },
  { rank: 5, user: 'The Revolting Blob', score: '124.0', highlight: false },
  { rank: 6, user: 'War Daddy', score: '120.5', highlight: false },
];

const LeaderboardCard: React.FC = () => (
  <CardShell>
    <div className="text-center mb-3">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-yellow/15 mb-1">
        <Trophy size={24} className="text-accent-yellow" />
      </div>
      <p className="text-badge font-bold uppercase tracking-widest text-gray-400 mb-0.5">Step 4 — Win</p>
      <p className="text-base font-heading font-bold text-white leading-tight">Most Points Wins</p>
    </div>
    <div className="space-y-1 flex-grow">
      {DEMO_LEADERBOARD.map((row) => (
        <div
          key={row.rank}
          className={`flex items-center rounded px-2 py-1 border ${
            row.highlight
              ? 'bg-accent-yellow/15 border-accent-yellow/60'
              : 'bg-primary/60 border-accent-yellow/10'
          }`}
        >
          <span
            className={`text-micro font-bold w-5 flex-shrink-0 ${
              row.highlight ? 'text-accent-yellow' : 'text-gray-400'
            }`}
          >
            #{row.rank}
          </span>
          <span
            className={`flex-grow text-caption truncate ${
              row.highlight ? 'text-accent-yellow font-bold' : 'text-gray-100'
            }`}
          >
            {row.user}
          </span>
          <span
            className={`text-caption font-bold flex-shrink-0 ml-2 ${
              row.highlight ? 'text-accent-yellow' : 'text-gray-300'
            }`}
          >
            {row.score}
          </span>
        </div>
      ))}
    </div>
  </CardShell>
);

// === SCORING CARDS ===

const ScoringIntroCard: React.FC = () => (
  <CardShell>
    <div className="flex-grow flex flex-col justify-center items-center text-center px-2 space-y-6">
      <p className="text-2xl font-heading font-bold text-accent-yellow leading-tight">
        8 Ways to Score
      </p>
      <p className="text-2xl font-heading font-bold text-accent-yellow leading-tight">
        Every Position Scores the Same
      </p>
    </div>
  </CardShell>
);

const StatLine: React.FC<{ icon: ReactNode; label: string; points: number; negative?: boolean }> = ({
  icon,
  label,
  points,
  negative,
}) => (
  <div className="flex items-center justify-between bg-primary/40 rounded-md px-3 py-2 border border-gray-700/40">
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-sm font-medium text-gray-100">{label}</span>
    </div>
    <span className={`text-lg font-heading font-bold ${negative ? 'text-red-400' : 'text-accent-yellow'}`}>
      {negative ? points : `+${points}`}
    </span>
  </div>
);

// Bucket palette: Attacking #e24b4a / Creating #78b4e6 are deliberate one-off
// editorial colors for this explainer (softened for legibility as large text
// on navy) — not drift from secondary/accent-light-blue. Do not tokenize.
const AttackingCard: React.FC = () => (
  <CardShell accentColor="#e24b4a">
    <div className="text-center mb-4">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-500/15 mb-2">
        <Icon iconNode={soccerBall} size={28} className="text-[#e24b4a]" />
      </div>
      <h3 className="text-xl font-heading font-bold uppercase tracking-wider text-[#e24b4a]">Attacking</h3>
    </div>
    <div className="space-y-2 flex-grow">
      <StatLine icon={<Icon iconNode={soccerBall} size={16} className="text-accent-yellow" />} label="Goal" points={8} />
      <StatLine icon={<Target size={16} className="text-accent-yellow" />} label="Shot on Target" points={3} />
      <StatLine icon={<Zap size={16} className="text-accent-yellow" />} label="Shot" points={1} />
    </div>
  </CardShell>
);

const CreatingCard: React.FC = () => (
  <CardShell accentColor="#78b4e6">
    <div className="text-center mb-4">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-400/15 mb-2">
        <KeyRound size={26} className="text-[#78b4e6]" />
      </div>
      <h3 className="text-xl font-heading font-bold uppercase tracking-wider text-[#78b4e6]">Creating</h3>
    </div>
    <div className="space-y-2 flex-grow">
      <StatLine icon={<KeyRound size={16} className="text-accent-yellow" />} label="Goal Creation" points={6} />
      <StatLine icon={<Handshake size={16} className="text-accent-yellow" />} label="Shot Creation" points={1.5} />
    </div>
    <div className="text-center mt-3 space-y-1.5">
      <p className="text-caption font-bold text-white leading-snug">
        Goal/Shot creations are the 2 offensive actions immediately leading to a goal/shot (assists, hockey assists, fouls won, shots leading to another shot, etc.).
      </p>
      <p className="text-caption font-bold text-accent-yellow leading-snug">
        Think of GCs like Passing TDs.
      </p>
    </div>
  </CardShell>
);

const ProgressingCard: React.FC = () => (
  <CardShell>
    <div className="text-center mb-4">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent-yellow/15 mb-2">
        <MoveRight size={26} className="text-accent-yellow" />
      </div>
      <h3 className="text-xl font-heading font-bold uppercase tracking-wider text-accent-yellow">Progressing</h3>
    </div>
    <div className="space-y-2 flex-grow">
      <StatLine icon={<MoveRight size={16} className="text-accent-yellow" />} label="Impact Pass" points={1} />
      <StatLine icon={<Footprints size={16} className="text-accent-yellow" />} label="Impact Carry" points={1} />
    </div>
    <div className="text-center mt-3 space-y-1.5">
      <p className="text-caption font-bold text-white leading-snug">
        Impact Passes/Carries are passes or carries ending in the attacking half that move the ball 10 yards closer to the goal than it has been recently.
      </p>
      <p className="text-caption font-bold text-accent-yellow leading-snug">
        Think of IPs/ICs like Passing/Rushing Yards.
      </p>
    </div>
  </CardShell>
);

const TurnoversCard: React.FC = () => (
  <CardShell accentColor="#e24b4a">
    <div className="text-center mb-4">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-500/15 mb-2">
        <XCircle size={26} className="text-gray-400" />
      </div>
      <h3 className="text-xl font-heading font-bold uppercase tracking-wider text-gray-300">Turnovers</h3>
    </div>
    <div className="space-y-2 flex-grow">
      <StatLine icon={<XCircle size={16} className="text-red-400" />} label="Dispossessed" points={-1} negative />
    </div>
    <p className="text-caption font-bold text-white text-center mt-3 leading-snug">
      Losing the ball under pressure.
    </p>
  </CardShell>
);

// === CTA CARD ===

const CtaCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="w-full h-[420px] bg-accent-yellow rounded-xl border-2 border-accent-yellow flex flex-col items-center justify-center p-5 hover:bg-accent-yellow/90 active:bg-accent-yellow/90 transition-colors"
  >
    <Trophy size={48} className="text-primary mb-4" />
    <p className="text-xl font-heading font-bold uppercase tracking-wider text-primary mb-2">Build Your Lineup</p>
    <div className="text-center px-2 space-y-1">
      <p className="text-sm font-heading font-bold uppercase tracking-wider text-primary">Same Scoring.</p>
      <p className="text-sm font-heading font-bold uppercase tracking-wider text-primary">Same Strategy.</p>
      <p className="text-sm font-heading font-bold uppercase tracking-wider text-primary">Different Football.</p>
    </div>
  </button>
);

// === MAIN COMPONENT ===

// Shared CTA target: next weekend contest (else soonest upcoming, else the
// lobby). Exported so the tabbed How To Play page can call it ONCE and hand
// the handler to both sections; `enabled: false` skips the fetch when a
// parent supplies the handler (one request per page, not one per section -
// review catch 2026-07-22).
export const useNextContestCta = (enabled: boolean = true) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [nextContestId, setNextContestId] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    fetchUpcomingContests(token, 14)
      .then((contests) => {
        const weekend = contests
          .filter((contest) => contest.is_weekend_contest)
          .sort(compareContestsCombinedFirst)[0];
        if (weekend) setNextContestId(weekend.id);
        else if (contests.length > 0) setNextContestId(contests[0].id);
      })
      .catch(() => {
        /* fall back to /contests */
      });
  }, [token, enabled]);

  const handleCtaClick = () => {
    navigate(nextContestId ? `/dfs/build/${nextContestId}` : '/contests');
  };
  return { nextContestId, handleCtaClick };
};

interface SectionProps {
  /** Parent-supplied CTA handler; when present the section skips its own
      next-contest fetch. */
  onCtaClick?: () => void;
}

export const ScoringSection: React.FC<SectionProps> = ({ onCtaClick }) => {
  const internal = useNextContestCta(!onCtaClick);
  const nextContestId = internal.nextContestId;
  const handleCtaClick = onCtaClick ?? internal.handleCtaClick;
  const scoringCards = useMemo(
    () => [
      <ScoringIntroCard key="intro" />,
      <AttackingCard key="attacking" />,
      <CreatingCard key="creating" />,
      <ProgressingCard key="progressing" />,
      <TurnoversCard key="turnovers" />,
      <CtaCard key="cta-2" onClick={handleCtaClick} />,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nextContestId, handleCtaClick]
  );
  return (
    <section>
      <div className="flex items-center gap-3 mb-4 md:mb-6 px-4">
        <div className="flex-1 h-px bg-gray-700/50" />
        <span className="text-lg md:text-2xl font-heading font-bold uppercase tracking-wider text-accent-yellow whitespace-nowrap">Scoring</span>
        <div className="flex-1 h-px bg-gray-700/50" />
      </div>
      <Carousel ariaLabel="Scoring categories">{scoringCards}</Carousel>
    </section>
  );
};

export const DailyHowToSection: React.FC<SectionProps> = ({ onCtaClick }) => {
  const internal = useNextContestCta(!onCtaClick);
  const nextContestId = internal.nextContestId;
  const handleCtaClick = onCtaClick ?? internal.handleCtaClick;
  const howToPlayCards = useMemo(
    () => [
      <EmptyLineupCard key="empty" />,
      <FilledLineupCard key="filled" />,
      <ScoredLineupCard key="scored" />,
      <LeaderboardCard key="leaderboard" />,
      <CtaCard key="cta-1" onClick={handleCtaClick} />,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nextContestId, handleCtaClick]
  );
  return (
    <section>
      <div className="flex items-center gap-3 mb-4 md:mb-6 px-4">
        <div className="flex-1 h-px bg-gray-700/50" />
        <span className="text-lg md:text-2xl font-heading font-bold uppercase tracking-wider text-accent-yellow whitespace-nowrap">How to Play</span>
        <div className="flex-1 h-px bg-gray-700/50" />
      </div>
      <Carousel ariaLabel="How to Play steps">{howToPlayCards}</Carousel>
    </section>
  );
};

const HowItWorksCarousel: React.FC = () => {
  const { user } = useAuth();
  const [howToPlayTab, setHowToPlayTab] = useState<'leagues' | 'daily'>('leagues');
  // One fetch for the shared scoring section and the Daily tab. The Leagues
  // tab has its own Start a League destination and needs no contest lookup.
  const { handleCtaClick } = useNextContestCta();

  const handleTabSelect = (tab: 'leagues' | 'daily') => {
    setHowToPlayTab(tab);
    mixpanelTrack('How To Play Tab Selected', {
      'User ID': user?.id,
      'Tab': tab,
      'Surface': 'contest_lobby',
    });
  };

  return (
    <div className="space-y-8 md:space-y-12">
      {/* Scoring leads: the target audience already knows daily fantasy, so the
          differentiated value (how football actions score) comes before the
          familiar how-it-works mechanics. */}
      <ScoringSection onCtaClick={handleCtaClick} />
      <div
        role="group"
        aria-label="Choose a fantasy format"
        className="flex justify-center gap-2"
      >
        <button
          type="button"
          aria-pressed={howToPlayTab === 'leagues'}
          aria-controls="contest-lobby-how-to-play-panel"
          onClick={() => handleTabSelect('leagues')}
          className={`min-h-11 rounded-lg px-5 text-sm font-bold uppercase tracking-wider transition-colors font-heading ${
            howToPlayTab === 'leagues'
              ? 'bg-accent-yellow text-primary'
              : 'border border-gray-700/60 bg-surface-card text-accent-light-blue hover:border-accent-yellow/50'
          }`}
        >
          Leagues
        </button>
        <button
          type="button"
          aria-pressed={howToPlayTab === 'daily'}
          aria-controls="contest-lobby-how-to-play-panel"
          onClick={() => handleTabSelect('daily')}
          className={`min-h-11 rounded-lg px-5 text-sm font-bold uppercase tracking-wider transition-colors font-heading ${
            howToPlayTab === 'daily'
              ? 'bg-accent-yellow text-primary'
              : 'border border-gray-700/60 bg-surface-card text-accent-light-blue hover:border-accent-yellow/50'
          }`}
        >
          Daily
        </button>
      </div>
      <div
        id="contest-lobby-how-to-play-panel"
        role="region"
        aria-label={`${howToPlayTab === 'leagues' ? 'Leagues' : 'Daily'} how to play`}
      >
        {howToPlayTab === 'leagues'
          ? <HowToPlayLeaguesCarousel />
          : <DailyHowToSection onCtaClick={handleCtaClick} />}
      </div>
    </div>
  );
};

export default HowItWorksCarousel;
